import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Eye, X } from 'lucide-react';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Pagination States
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  // Date Range Filter States
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // View Message Modal State
  const [selectedMessage, setSelectedMessage] = useState(null);

  const API_CONTACT_MESSAGES = 'https://metapi.emedhatechnologies.com/api/students/contact-messages';

  useEffect(() => {
    fetchContactMessages();
  }, []);

  const fetchContactMessages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_CONTACT_MESSAGES);
      if (response.data.success) {
        setMessages(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  // Search & Date Filtering Logic
  const filteredMessages = messages.filter((item) => {
    const term = searchTerm.toLowerCase();
    const name = (item.full_name || '').toLowerCase();
    const email = (item.email_address || '').toLowerCase();
    const phone = (item.phone_number || '').toString().toLowerCase();
    const subject = (item.subject || '').toLowerCase();
    const msg = (item.message || '').toLowerCase();

    const matchesSearch =
      name.includes(term) ||
      email.includes(term) ||
      phone.includes(term) ||
      subject.includes(term) ||
      msg.includes(term);

    let matchesDate = true;
    if (item.created_at) {
      const itemDate = new Date(item.created_at).toISOString().substring(0, 10);
      if (fromDate && itemDate < fromDate) matchesDate = false;
      if (toDate && itemDate > toDate) matchesDate = false;
    }

    return matchesSearch && matchesDate;
  });

  // Pagination Logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMessages = filteredMessages.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMessages.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Export to Excel
  const handleExportToExcel = () => {
    if (filteredMessages.length === 0) {
      alert('No messages available to export for the selected filters.');
      return;
    }

    const exportData = filteredMessages.map((item, index) => ({
      'S.No': index + 1,
      'Full Name': item.full_name || 'N/A',
      'Email Address': item.email_address || 'N/A',
      'Phone Number': item.phone_number || 'N/A',
      'Subject': item.subject || 'General Enquiry',
      'Message': item.message || '',
      'Received Date': item.created_at
        ? new Date(item.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
          })
        : 'N/A'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contact_Messages');

    const fileName = `Contact_Messages_Export_${new Date().toISOString().substring(0, 10)}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return isNaN(d.getTime())
      ? dateStr
      : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div style={styles.container}>
      {/* Header Section */}
      <div style={styles.headerContainer}>
        <div>
          <h2 style={styles.title}>Contact Messages</h2>
          <p style={styles.subtitle}>View and manage incoming queries submitted via the Contact &amp; Enquiry form.</p>
        </div>
        <span style={styles.counterBadge}>
          Total: {filteredMessages.length}{' '}
          {filteredMessages.length !== messages.length ? `(filtered from ${messages.length})` : ''}
        </span>
      </div>

      {/* Search & Date Range Filter Bar */}
      <div style={styles.filterControlBar}>
        <div style={styles.searchInputWrapper}>
          <svg style={styles.searchIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            placeholder="Search name, email, phone, subject..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={styles.searchInput}
          />
          {searchTerm && (
            <button onClick={() => { setSearchTerm(''); setCurrentPage(1); }} style={styles.clearSearchBtn}>
              Clear
            </button>
          )}
        </div>

        <div style={styles.dateFilterGroup}>
          <div style={styles.dateBox}>
            <label style={styles.dateLabel}>From:</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
              style={styles.dateInput}
            />
          </div>

          <div style={styles.dateBox}>
            <label style={styles.dateLabel}>To:</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
              style={styles.dateInput}
            />
          </div>

          {(fromDate || toDate) && (
            <button
              type="button"
              onClick={() => { setFromDate(''); setToDate(''); setCurrentPage(1); }}
              style={styles.resetDateBtn}
            >
              Reset Dates
            </button>
          )}

          <button type="button" onClick={handleExportToExcel} style={styles.exportBtn}>
            📥 Export Excel
          </button>
        </div>
      </div>

      {/* Main Table Card */}
      <div style={styles.card}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>Loading contact messages...</p>
          </div>
        ) : (
          <>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr style={styles.trHead}>
                    <th style={{ ...styles.th, width: '45px' }}>#</th>
                    <th style={styles.th}>Sender</th>
                    <th style={styles.th}>Contact Info</th>
                    <th style={styles.th}>Subject Category</th>
                    <th style={styles.th}>Message Preview</th>
                    <th style={styles.th}>Date</th>
                    <th style={{ ...styles.th, textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentMessages.length > 0 ? (
                    currentMessages.map((item, index) => (
                      <tr key={item.id} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                        <td style={{ ...styles.td, color: '#94a3b8', fontWeight: '600' }}>
                          {indexOfFirstItem + index + 1}
                        </td>

                        {/* Sender Info */}
                        <td style={styles.td}>
                          <div style={styles.profileCell}>
                            <div style={styles.avatar}>
                              {item.full_name ? item.full_name.charAt(0).toUpperCase() : 'U'}
                            </div>
                            <div style={styles.senderName}>{item.full_name}</div>
                          </div>
                        </td>

                        {/* Contact Info */}
                        <td style={styles.td}>
                          <div style={styles.contactStack}>
                            <span style={styles.contactText}>{item.email_address}</span>
                            <span style={styles.contactSub}>{item.phone_number || 'No phone'}</span>
                          </div>
                        </td>

                        {/* Subject */}
                        <td style={styles.td}>
                          <span style={styles.subjectBadge}>{item.subject || 'General Enquiry'}</span>
                        </td>

                        {/* Message Preview */}
                        <td style={styles.td}>
                          <div style={styles.messagePreviewText}>
                            {item.message || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>No message</span>}
                          </div>
                        </td>

                        {/* Date */}
                        <td style={{ ...styles.td, color: '#64748b', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {formatDate(item.created_at)}
                        </td>

                        {/* Action */}
                        <td style={{ ...styles.td, textAlign: 'center' }}>
                          <button
                            onClick={() => setSelectedMessage(item)}
                            style={styles.viewBtn}
                            title="View Full Message"
                          >
                            <Eye size={13} style={{ marginRight: '4px' }} /> View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={styles.emptyState}>No matching contact messages found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {filteredMessages.length > 0 && (
              <div style={styles.paginationContainer}>
                <span style={styles.paginationInfo}>
                  Showing <b>{indexOfFirstItem + 1}</b> to <b>{Math.min(indexOfLastItem, filteredMessages.length)}</b> of <b>{filteredMessages.length}</b> entries
                </span>

                <div style={styles.paginationControls}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{ ...styles.pageBtn, opacity: currentPage === 1 ? 0.4 : 1 }}
                  >
                    Previous
                  </button>
                  <span style={styles.pageIndicator}>Page {currentPage} of {totalPages || 1}</span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages || totalPages === 0}
                    style={{ ...styles.pageBtn, opacity: (currentPage === totalPages || totalPages === 0) ? 0.4 : 1 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* VIEW MESSAGE MODAL */}
      {selectedMessage && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalBox}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Contact Message Details</h3>
                <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                  Received on {formatDate(selectedMessage.created_at)}
                </span>
              </div>
              <button onClick={() => setSelectedMessage(null)} style={styles.modalCloseBtn}>
                <X size={18} />
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.modalGrid}>
                <div style={styles.modalCol}>
                  <span style={styles.metaLabel}>Sender Name</span>
                  <span style={styles.metaValue}>{selectedMessage.full_name}</span>
                </div>
                <div style={styles.modalCol}>
                  <span style={styles.metaLabel}>Subject Category</span>
                  <span style={styles.metaValue}>{selectedMessage.subject || 'General Enquiry'}</span>
                </div>
                <div style={styles.modalCol}>
                  <span style={styles.metaLabel}>Email Address</span>
                  <a href={`mailto:${selectedMessage.email_address}`} style={styles.metaLink}>
                    {selectedMessage.email_address}
                  </a>
                </div>
                <div style={styles.modalCol}>
                  <span style={styles.metaLabel}>Phone Number</span>
                  {selectedMessage.phone_number ? (
                    <a href={`tel:${selectedMessage.phone_number}`} style={styles.metaLink}>
                      {selectedMessage.phone_number}
                    </a>
                  ) : (
                    <span style={{ color: '#94a3b8' }}>N/A</span>
                  )}
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <span style={styles.metaLabel}>Message Content</span>
                <div style={styles.fullMessageBox}>
                  {selectedMessage.message || 'No additional message text provided.'}
                </div>
              </div>
            </div>

            <div style={styles.modalActions}>
              {selectedMessage.phone_number && (
                <a
                  href={`https://wa.me/${selectedMessage.phone_number.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.whatsappBtn}
                >
                  Reply via WhatsApp
                </a>
              )}
              <button onClick={() => setSelectedMessage(null)} style={styles.closeActionBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", system-ui, sans-serif', color: '#1e293b', padding: '20px' },
  headerContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  title: { fontSize: '22px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px 0' },
  subtitle: { fontSize: '13px', color: '#64748b', margin: 0 },
  counterBadge: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#ffffff', color: '#334155', padding: '6px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: '1px solid #e2e8f0' },

  filterControlBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' },
  searchInputWrapper: { position: 'relative', display: 'flex', alignItems: 'center', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '0 12px', flex: 1, maxWidth: '380px' },
  searchIcon: { width: '16px', height: '16px', strokeWidth: 2, color: '#94a3b8', marginRight: '8px' },
  searchInput: { width: '100%', border: 'none', outline: 'none', padding: '10px 0', fontSize: '13px', color: '#1e293b', background: 'transparent' },
  clearSearchBtn: { background: 'transparent', border: 'none', color: '#64748b', fontSize: '12px', cursor: 'pointer', fontWeight: '500' },

  dateFilterGroup: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  dateBox: { display: 'flex', alignItems: 'center', gap: '4px', background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '4px 8px' },
  dateLabel: { fontSize: '11px', color: '#64748b', fontWeight: '600' },
  dateInput: { border: 'none', outline: 'none', fontSize: '12px', color: '#1e293b', background: 'transparent', cursor: 'pointer' },
  resetDateBtn: { background: '#fff1f2', border: '1px solid #fecaca', color: '#b91c1c', padding: '6px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer' },
  exportBtn: { background: '#0f172a', color: '#ffffff', border: 'none', padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px', boxShadow: '0 2px 4px rgba(15, 23, 42, 0.1)' },

  card: { background: '#ffffff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
  tableWrapper: { width: '100%', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' },
  trHead: { backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' },
  th: { padding: '12px 14px', color: '#475569', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase' },
  trEven: { backgroundColor: '#ffffff' },
  trOdd: { backgroundColor: '#fafbfc' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle', color: '#334155' },

  profileCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '600', fontSize: '13px' },
  senderName: { fontWeight: '600', color: '#0f172a', fontSize: '13px' },
  contactStack: { display: 'flex', flexDirection: 'column', gap: '3px' },
  contactText: { color: '#0f172a', fontSize: '12.5px', fontWeight: '500' },
  contactSub: { color: '#64748b', fontSize: '11.5px' },
  subjectBadge: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', padding: '3px 8px', borderRadius: '6px', fontSize: '11.5px', fontWeight: '600', display: 'inline-block' },
  messagePreviewText: { maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#475569', fontSize: '12.5px' },
  viewBtn: { background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },

  paginationContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #e2e8f0', backgroundColor: '#fafafa' },
  paginationInfo: { fontSize: '12px', color: '#64748b' },
  paginationControls: { display: 'flex', alignItems: 'center', gap: '10px' },
  pageBtn: { backgroundColor: '#ffffff', border: '1px solid #cbd5e1', color: '#334155', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', cursor: 'pointer' },
  pageIndicator: { fontSize: '12px', fontWeight: '600', color: '#475569' },

  loadingContainer: { padding: '40px', textAlign: 'center' },
  spinner: { width: '28px', height: '28px', border: '3px solid #e2e8f0', borderTop: '3px solid #2563eb', borderRadius: '50%', margin: '0 auto 12px auto' },
  loadingText: { color: '#64748b', fontSize: '13px' },
  emptyState: { textAlign: 'center', padding: '36px', color: '#64748b', fontSize: '13px' },

  // View Modal Styles
  modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.45)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalBox: { backgroundColor: '#ffffff', width: '100%', maxWidth: '520px', borderRadius: '16px', padding: '24px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15)', boxSizing: 'border-box' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' },
  modalTitle: { fontSize: '16px', fontWeight: '700', color: '#0f172a', margin: '0 0 2px 0' },
  modalCloseBtn: { background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' },
  modalBody: { marginTop: '14px' },
  modalGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' },
  modalCol: { display: 'flex', flexDirection: 'column', gap: '2px' },
  metaLabel: { fontSize: '10.5px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' },
  metaValue: { fontSize: '13px', fontWeight: '600', color: '#0f172a' },
  metaLink: { fontSize: '13px', fontWeight: '600', color: '#2563eb', textDecoration: 'none' },
  fullMessageBox: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#334155', lineHeight: '1.6', marginTop: '6px', whiteSpace: 'pre-wrap', maxHeight: '180px', overflowY: 'auto' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '18px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' },
  closeActionBtn: { backgroundColor: '#f1f5f9', border: '1px solid #cbd5e1', color: '#475569', padding: '8px 16px', borderRadius: '6px', fontSize: '12.5px', fontWeight: '600', cursor: 'pointer' },
  whatsappBtn: { backgroundColor: '#10b981', color: '#ffffff', textDecoration: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '12.5px', fontWeight: '600', display: 'inline-flex', alignItems: 'center' }
};

export default ContactMessages;